import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin, Star } from "lucide-react";
import { IMG, MAPS_URL } from "./data";
import { MaskLine, Eyebrow, EASE } from "./Reveal";
import { scrollToId } from "./lib";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const nunY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden flex flex-col"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img
          src={IMG.hero}
          alt="Kebabs searing over charcoal flames"
          className="h-[120%] w-full object-cover brightness-[0.42] saturate-[0.85]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/30 to-charcoal" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-transparent to-charcoal/40" />
      <div className="ember-glow absolute -bottom-32 left-1/4 h-[60vh] w-[70vw] pointer-events-none" />

      {/* Arabic letter Nun watermark */}
      <motion.span
        style={{ y: nunY }}
        aria-hidden="true"
        className="absolute right-[-6vw] top-[2%] font-arabic text-[52vw] md:text-[38vw] leading-none text-ember/[0.06] select-none pointer-events-none"
      >
        ن
      </motion.span>

      {/* Content */}
      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 flex-1 flex flex-col justify-end pb-16 md:pb-24 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-10 bg-gold/60" />
          <Eyebrow>Mallikatte · Kadri · Mangaluru</Eyebrow>
        </motion.div>

        <h1 className="font-display font-bold uppercase leading-[0.88] tracking-tight text-[15vw] sm:text-[14vw] md:text-[10.5vw]">
          <MaskLine delay={0.25}>Charcoal.</MaskLine>
          <MaskLine delay={0.4}>Smoke &amp;</MaskLine>
          <MaskLine delay={0.55} className="text-ember italic">
            Kebabs.
          </MaskLine>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <MaskLine delay={0.75} className="max-w-md">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
              className="block text-dim text-base md:text-lg leading-relaxed"
            >
              Authentic Arab skewers, lapeta rolls and mezze — char-grilled
              nightly over real embers in the heart of Mangaluru.
            </motion.span>
          </MaskLine>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-menu"
              onClick={() => scrollToId("menu")}
              className="rounded-full bg-ember hover:bg-emberglow text-charcoal font-semibold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_0_40px_rgba(214,66,43,0.4)]"
            >
              Explore the menu
            </button>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-directions"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-gold text-bone font-medium text-sm tracking-widest uppercase px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
            >
              <MapPin className="w-4 h-4 text-gold" />
              Get directions
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom info bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.8 }}
        className="relative z-10 border-t border-white/10 bg-charcoal/40 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-dim">
          <span data-testid="hero-badge-open" className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-ember animate-pulse" />
            Open daily 4:00 PM — 11:30 PM
          </span>
          <span className="hidden sm:flex items-center gap-2">
            <Star className="w-3.5 h-3.5 fill-gold text-gold" />
            5.0 rated on Google
          </span>
          <span className="hidden md:flex items-center gap-2 ml-auto">
            Scroll
            <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
          </span>
        </div>
      </motion.div>
    </section>
  );
}
